/**
 * アイドルロック — ユーザーが指定時間操作しなかったら `onLock` コールバックを発火する。
 *
 * 設計判断 (T-B4):
 *   - **インスタンス型**: `startIdleLock()` がクロージャ内に状態を閉じ込め、
 *     `{ stop }` を返す。module-level singleton state を使わない (L-15 教訓)。
 *     複数インスタンス共存可能 (将来「設定別タイマ」「複数 vault 同時管理」等)。
 *   - **DI**: `IdleLockDeps` で `setTimeout` / `clearTimeout` / `now` /
 *     `win` / `doc` を差し替え可能にし、fake timer + mock target で確定的にテスト。
 *   - **debounce 1 秒**: 高頻度イベント (mousemove 等) で reset を呼びすぎない。
 *     最後の reset から 1 秒未満なら無視する (Date.now ベース)。
 *   - **visibilitychange は `visibility=visible` 時のみ reset**: タブ非アクティブ
 *     中にユーザーが画面の前にいない可能性が高いため、復帰時のみカウンタを再起。
 *   - **expire 後は自己 stop**: `onLock` 実行後はインスタンスを停止状態にし、
 *     listener を全解除。呼出側で再度 `startIdleLock()` を呼ぶ運用。
 *   - **passive listener**: scroll / mousemove は passive で登録し、ページの
 *     スクロール挙動を阻害しない。
 *
 * 既定値:
 *   - idleMs = 30 分 (UX 重視で長め設定、preferences.types と一致)。
 *     UI 選択肢は 1 / 5 / 15 / 30 / 60 分 + ブラウザを閉じたとき の 6 値 (ookey-ios と統一)。
 *     backend DTO `@IsIn(...)` で同 6 値を強制している。
 *   - 監視イベント: mousemove / keydown / scroll (window) + visibilitychange (document)
 */

export const IDLE_LOCK_DEFAULT_MS = 30 * 60 * 1000 // 30 分 (preferences.types と一致)
export const IDLE_LOCK_RESET_DEBOUNCE_MS = 1_000

/** 「ブラウザを閉じたときのみロック」を表す sentinel。idleMs===0 で timer を無効化し、
 * sessionStore (in-memory) はタブ/ウィンドウクローズで自然に消える挙動に委ねる。 */
export const IDLE_LOCK_BROWSER_CLOSE = 0

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'scroll'] as const
type ActivityEvent = (typeof ACTIVITY_EVENTS)[number]

export interface IdleLockEventTarget {
  addEventListener(type: string, listener: () => void, options?: AddEventListenerOptions): void
  removeEventListener(type: string, listener: () => void): void
}

export interface IdleLockDocumentLike extends IdleLockEventTarget {
  readonly visibilityState: DocumentVisibilityState
}

export interface IdleLockDeps {
  setTimeout: (fn: () => void, ms: number) => ReturnType<typeof globalThis.setTimeout>
  clearTimeout: (id: ReturnType<typeof globalThis.setTimeout>) => void
  now: () => number
  win: IdleLockEventTarget
  doc: IdleLockDocumentLike
}

export interface IdleLockOptions {
  /** アイドル判定までの ms。既定 30 分 (IDLE_LOCK_DEFAULT_MS)。
   * `IDLE_LOCK_BROWSER_CLOSE` (= 0) を渡すと timer / listener 一切張らず no-op。 */
  idleMs?: number

  /** タイマー満了時に呼ばれる。`sessionStore.clear()` + `navigate('lock')` 等は呼出側で組合せ。 */
  onLock: () => void

  /** テスト用差し替え。本番では指定不要。 */
  deps?: Partial<IdleLockDeps>
}

export interface IdleLockHandle {
  /** イベント listener 解除 + 進行中タイマー clear。idempotent。 */
  stop: () => void
}

const browserDeps = (): IdleLockDeps => ({
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: id => globalThis.clearTimeout(id),
  now: () => Date.now(),
  win: typeof window === 'undefined' ? noopTarget : window,
  doc: typeof document === 'undefined' ? { ...noopTarget, visibilityState: 'visible' } : document,
})

const noopTarget: IdleLockEventTarget = {
  addEventListener: () => {},
  removeEventListener: () => {},
}

/** 「ブラウザを閉じたとき」 sentinel 以外で受理する最小 ms。
 * backend DTO は @IsIn で 6 値に固定済だが、レガシーデータ / クロックずれ /
 * future migration での誤値に備え、client 側でも 1 分未満は 1 分に切り上げる
 * (review I-2、self-DoS 防御の二重化)。 */
const IDLE_LOCK_MIN_MS = 60_000

export const startIdleLock = (opts: IdleLockOptions): IdleLockHandle => {
  const rawIdleMs = opts.idleMs ?? IDLE_LOCK_DEFAULT_MS

  // 「ブラウザを閉じたときのみロック」モード: timer / listener を一切張らず、
  // ロックは sessionStore の in-memory 性 (タブクローズで自然消滅) に委ねる。
  if (rawIdleMs === IDLE_LOCK_BROWSER_CLOSE) {
    return { stop: (): void => {} }
  }

  const idleMs = Math.max(rawIdleMs, IDLE_LOCK_MIN_MS)

  const onLock = opts.onLock
  const d: IdleLockDeps = { ...browserDeps(), ...opts.deps }

  let timerId: ReturnType<typeof globalThis.setTimeout> | null = null
  let lastResetAt = d.now()
  let stopped = false

  const armTimer = (): void => {
    if (timerId !== null) d.clearTimeout(timerId)
    timerId = d.setTimeout(expire, idleMs)
    lastResetAt = d.now()
  }

  const cleanup = (): void => {
    if (timerId !== null) {
      d.clearTimeout(timerId)
      timerId = null
    }
    for (const ev of ACTIVITY_EVENTS) {
      d.win.removeEventListener(ev, onActivity)
    }
    d.doc.removeEventListener('visibilitychange', onVisibility)
  }

  function expire(): void {
    if (stopped) return
    stopped = true
    cleanup()
    onLock()
  }

  function onActivity(): void {
    if (stopped) return
    if (d.now() - lastResetAt < IDLE_LOCK_RESET_DEBOUNCE_MS) return
    armTimer()
  }

  function onVisibility(): void {
    if (stopped) return
    if (d.doc.visibilityState !== 'visible') return
    armTimer()
  }

  for (const ev of ACTIVITY_EVENTS satisfies readonly ActivityEvent[]) {
    d.win.addEventListener(ev, onActivity, { passive: true })
  }
  d.doc.addEventListener('visibilitychange', onVisibility)

  armTimer()

  return {
    stop: (): void => {
      if (stopped) return
      stopped = true
      cleanup()
    },
  }
}
